import { isEqual, isNil, omit } from 'es-toolkit'
import { jwtDecode } from 'jwt-decode'

import {
  UserAuthorizationResultsSchemaRedirectTo,
  type MfaLevel,
  type TwoFactorAuthType,
  type TwoFactorEnforcementState,
} from '@/api/types'
import { queryClient } from '@/lib/query-client'
import { app } from '@/states/app-state'

import { logService } from './log-service'
import { localStorageService, sessionStorageService } from './storage-service'

interface TokenPayload {
  aud: string // audience
  exp: number // expiration
  iat: number // issued at
  iss: string // issuer
  BrandsPay_Conum?: string
  BrandsPay_Email?: string
  BrandsPay_Empnum?: string
  BrandsPay_FName?: string
  BrandsPay_LName?: string
  Email?: string
  PhoneNumber?: string
  RedirectTo?: string
  RememberBrowserGuid?: string
  TwoFactorType?: string
  UserId?: string
  MinMfaLevel?: string
  PhoneNumberConfirmed?: string
  HasMfaMethod?: string
  AllowedMfaMethods?: string
  MfaEnforcementDate?: string
  MfaEnforcementState?: string
  TwoFactorAudience?: string
  ThenResetPassword?: string
  EssProfilesOnly?: string
  MfaSkipped?: string
  EmployeeOnboardingPro?: string
  CustomReportsEnabled?: string
}

type RawClaim =
  | 'TwoFactorType'
  | 'MinMfaLevel'
  | 'PhoneNumberConfirmed'
  | 'HasMfaMethod'
  | 'AllowedMfaMethods'
  | 'MfaEnforcementState'
  | 'ThenResetPassword'
  | 'EssProfilesOnly'
  | 'MfaSkipped'

interface TokenPayloadParsed extends Omit<TokenPayload, RawClaim> {
  TwoFactorType?: TwoFactorAuthType
  MinMfaLevel?: MfaLevel
  PhoneNumberConfirmed: boolean
  HasMfaMethod: boolean
  AllowedMfaMethods: TwoFactorAuthType[]
  // Missing on full JWTs and on tokens issued before the claim existed.
  MfaEnforcementState?: TwoFactorEnforcementState
  ThenResetPassword: boolean
  EssProfilesOnly: boolean
  MfaSkipped: boolean
}

// Tokens issued mid-login; they only unlock the /auth pages that finish the step they name.
const INTERMEDIATE_REDIRECTS: UserAuthorizationResultsSchemaRedirectTo[] = [
  UserAuthorizationResultsSchemaRedirectTo.TwoFactorAuth,
  UserAuthorizationResultsSchemaRedirectTo.TwoFactorAuthPrompt,
  UserAuthorizationResultsSchemaRedirectTo.ResetPasswordVerify,
  UserAuthorizationResultsSchemaRedirectTo.ResetPassword,
]

function parseBool(value?: string) {
  return value?.toLowerCase() === 'true'
}

function parseNumber(value?: string) {
  if (!value) return undefined
  const parsed = Number.parseInt(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

function parseNumberList(value?: string) {
  if (!value) return []
  return value
    .split(',')
    .map((part) => parseNumber(part.trim()))
    .filter((part): part is number => !isNil(part))
}

// Claims minus the volatile ones, so a plain token refresh looks identical.
function identityOf(token?: string | null) {
  if (!token) return null
  try {
    return omit(jwtDecode<Record<string, unknown>>(token), ['iat', 'exp', 'nbf'])
  } catch {
    return null
  }
}

export const authService = {
  saveToken(token?: string | null) {
    if (!token) return
    // Only a real identity change (login, company/profile switch) may drop the previous user's cached data.
    const isSameIdentity = isEqual(identityOf(token), identityOf(this.getToken()))
    localStorageService.setJwtToken(token)
    sessionStorageService.setJwtToken(token)
    if (!isSameIdentity) queryClient.clear()
  },
  saveJwtGuid(jwtGuid?: string | null) {
    if (!jwtGuid) return
    sessionStorageService.setJwtGuid(jwtGuid)
  },
  removeToken() {
    localStorageService.removeJwtToken()
    sessionStorageService.removeJwtToken()
    queryClient.clear()
  },
  getToken() {
    return sessionStorageService.getJwtToken() || localStorageService.getJwtToken()
  },
  getJwtGuid() {
    return sessionStorageService.getJwtGuid()
  },
  getDecodedToken() {
    try {
      const token = this.getToken()
      if (!token) return null

      const data = jwtDecode<TokenPayload>(token)

      const result: TokenPayloadParsed = {
        ...data,
        TwoFactorType: parseNumber(data.TwoFactorType) as TwoFactorAuthType | undefined,
        MinMfaLevel: parseNumber(data.MinMfaLevel) as MfaLevel | undefined,
        PhoneNumberConfirmed: parseBool(data.PhoneNumberConfirmed),
        HasMfaMethod: parseBool(data.HasMfaMethod),
        AllowedMfaMethods: parseNumberList(data.AllowedMfaMethods) as TwoFactorAuthType[],
        MfaEnforcementState: parseNumber(data.MfaEnforcementState) as TwoFactorEnforcementState | undefined,
        ThenResetPassword: parseBool(data.ThenResetPassword),
        EssProfilesOnly: parseBool(data.EssProfilesOnly),
        MfaSkipped: parseBool(data.MfaSkipped),
      }

      return result
    } catch (error) {
      logService.error('Error decoding JWT token', { error })
      return null
    }
  },
  isTokenExpired() {
    const decodedToken = this.getDecodedToken()
    if (!decodedToken) return true

    const expirationTime = decodedToken.exp * 1000

    if (!window.serverTimeOffset) return expirationTime < Date.now()

    const serverTime = Date.now() + (window.serverTimeOffset ?? 0)
    const expired = expirationTime < serverTime

    if (expired && !window.location.href?.endsWith('/login') && !window.location.href?.endsWith('onboarding/new')) {
      app.showErrorToast(
        'Your access token has expired. Please login again. If the issue persists, check the date and time on your device to ensure they are correct.',
      )
    }

    return expired
  },
  isUserAuthenticated() {
    const decodedToken = this.getDecodedToken()
    return !this.isTokenExpired() && !!decodedToken?.UserId
  },
  isCompanyAuthenticated() {
    const decodedToken = this.getDecodedToken()
    return !this.isTokenExpired() && !!decodedToken?.BrandsPay_Conum
  },
  // The issuer separates a Paydeck login from an ESS or onboarding token.
  isPaydeckSession() {
    return this.getDecodedToken()?.iss === 'BrandsWeb'
  },
  getRedirectTo() {
    return parseNumber(this.getDecodedToken()?.RedirectTo) as UserAuthorizationResultsSchemaRedirectTo | undefined
  },
  isIpRestricted() {
    return this.isUserAuthenticated() && this.getRedirectTo() === UserAuthorizationResultsSchemaRedirectTo.IPRestricted
  },
  // True when the stored token names one of the given login steps.
  isRedirectToSet(...redirectTo: UserAuthorizationResultsSchemaRedirectTo[]) {
    const current = this.getRedirectTo()
    return this.isUserAuthenticated() && !isNil(current) && redirectTo.includes(current)
  },
  isIntermediateToken() {
    const current = this.getRedirectTo()
    return !isNil(current) && INTERMEDIATE_REDIRECTS.includes(current)
  },
  // Intermediate tokens must not outlive the /auth flow they were issued for.
  clearIntermediateToken() {
    if (!this.getToken() || !this.isIntermediateToken()) return
    logService.info('Clearing intermediate auth token')
    this.removeToken()
  },
  isEmployeeOnboardingProEnabled() {
    const decodedToken = this.getDecodedToken()
    return decodedToken?.EmployeeOnboardingPro?.toLowerCase() === 'true'
  },
  isCustomReportsEnabled() {
    const decodedToken = this.getDecodedToken()
    return decodedToken?.CustomReportsEnabled?.toLowerCase() === 'true'
  },
}
